import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, CalendarDays, CornerDownLeft, Loader2, Search, X } from "lucide-react";
import { Avatar } from "@/components/ContactRow";
import { useEvents } from "@/hooks/queries";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/toto";

function useDebounced(v, ms = 200) {
  const [d, setD] = useState(v);
  useEffect(() => {
    const t = setTimeout(() => setD(v), ms);
    return () => clearTimeout(t);
  }, [v, ms]);
  return d;
}

function Mark({ text = "", q }) {
  const i = q ? text.toLowerCase().indexOf(q.toLowerCase()) : -1;
  if (i < 0) return <>{text}</>;
  return <>{text.slice(0, i)}<mark className="bg-[#E5E5FD] text-[#0000EC] rounded-sm">{text.slice(i, i + q.length)}</mark>{text.slice(i + q.length)}</>;
}

const matchedField = (c, q) => {
  const l = q.toLowerCase();
  for (const [k, label] of [["phone", "Phone"], ["email", "Email"], ["city", "City"], ["alternatePhone", "Alt. phone"], ["website", "Website"]])
    if (c[k] && String(c[k]).toLowerCase().includes(l)) return `${label}: ${c[k]}`;
  return null;
};

export default function SearchSuggest() {
  const nav = useNavigate();
  const box = useRef(null);
  const input = useRef(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const term = useDebounced(q.trim());
  const contacts = useQuery({ queryKey: ["contacts", { search: term, suggest: true }], queryFn: () => api.get(`/contacts?search=${encodeURIComponent(term)}`), enabled: term.length > 0, staleTime: 30000 });
  const events = (useEvents({ sort: "date" }).data ?? []).filter((e) => term && `${e.name} ${e.location ?? ""}`.toLowerCase().includes(term.toLowerCase())).slice(0, 3);
  const people = (contacts.data ?? []).slice(0, 6);
  const items = [
    ...people.map((c) => ({ key: `c${c.id}`, go: `/contacts/${c.id}` })),
    ...events.map((e) => ({ key: `e${e.id}`, go: `/events/${e.id}` })),
    { key: "all", go: `/contacts?q=${encodeURIComponent(q.trim())}` },
  ];

  useEffect(() => setActive(0), [term]);
  useEffect(() => {
    const close = (e) => !box.current?.contains(e.target) && setOpen(false);
    const slash = (e) => {
      if (e.key === "/" && !["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName)) { e.preventDefault(); input.current?.focus(); }
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", slash);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", slash); };
  }, []);

  const go = (path) => { setOpen(false); setQ(""); input.current?.blur(); nav(path); };
  const onKey = (e) => {
    if (!open || !q.trim()) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, items.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); go(items[active].go); }
    else if (e.key === "Escape") setOpen(false);
  };
  const row = (i) => `w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${active === i ? "bg-[#F5F5FF]" : "hover:bg-[#F8FAFC]"}`;
  const show = open && q.trim().length > 0;

  return (
    <div ref={box} className="relative flex-1 max-w-xl mr-auto" data-testid="search-suggest">
      <Search className="absolute left-4 top-[13px] w-5 h-5 text-[#64748B] pointer-events-none" />
      <input ref={input} className="t-input h-[46px] pl-12 pr-16 text-[15px]" placeholder="Search contacts, companies, events…" value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onKeyDown={onKey} data-testid="topbar-search" />
      {q ? (
        <button className="absolute right-3 top-[11px] w-6 h-6 rounded-full bg-[#E2E8F0] flex items-center justify-center" onClick={() => { setQ(""); input.current?.focus(); }} data-testid="topbar-search-clear"><X className="w-3.5 h-3.5" /></button>
      ) : <kbd className="absolute right-3 top-[12px] text-[12px] font-semibold text-[#64748B] border border-[#CBD5E1] rounded-md px-1.5 py-0.5 bg-white">/</kbd>}

      {show && (
        <div className="absolute left-0 right-0 top-[54px] z-50 rounded-[18px] bg-white border border-[#E2E8F0] shadow-[0_24px_60px_-20px_rgba(15,23,42,.35)] p-2 max-h-[70vh] overflow-y-auto fade-up" data-testid="search-suggestions">
          <p className="px-3 pt-1 pb-1.5 text-[11px] font-bold uppercase tracking-wide text-[#94A3B8] flex items-center gap-2">Contacts {contacts.isFetching && <Loader2 className="w-3 h-3 animate-spin" />}</p>
          {people.map((c, i) => {
            const extra = matchedField(c, term);
            return (
              <button key={c.id} className={row(i)} onMouseEnter={() => setActive(i)} onClick={() => go(`/contacts/${c.id}`)} data-testid={`suggest-contact-${c.id}`}>
                <Avatar name={c.name || c.companyName} size={36} />
                <span className="flex-1 min-w-0">
                  <span className="block text-[14px] font-bold truncate"><Mark text={c.name || c.companyName} q={term} /></span>
                  <span className="block text-[12px] text-[#64748B] truncate">{extra ? <Mark text={extra} q={term} /> : <Mark text={[c.designation, c.companyName].filter(Boolean).join(" · ")} q={term} />}</span>
                </span>
                {active === i && <CornerDownLeft className="w-4 h-4 text-[#94A3B8]" />}
              </button>
            );
          })}
          {!contacts.isFetching && term && !people.length && <p className="px-3 py-2 text-[13px] text-[#64748B]" data-testid="suggest-no-contacts">No contacts match “{term}”.</p>}
          {events.length > 0 && <p className="px-3 pt-3 pb-1.5 text-[11px] font-bold uppercase tracking-wide text-[#94A3B8]">Events</p>}
          {events.map((e, j) => {
            const i = people.length + j;
            return (
              <button key={e.id} className={row(i)} onMouseEnter={() => setActive(i)} onClick={() => go(`/events/${e.id}`)} data-testid={`suggest-event-${e.id}`}>
                <span className="w-9 h-9 rounded-[10px] bg-[#E5E5FD] flex items-center justify-center"><CalendarDays className="w-4 h-4 text-[#0000EC]" /></span>
                <span className="flex-1 min-w-0"><span className="block text-[14px] font-bold truncate"><Mark text={e.name} q={term} /></span><span className="block text-[12px] text-[#64748B] truncate">{fmtDate(e.eventDate)}{e.location ? ` · ${e.location}` : ""}</span></span>
              </button>
            );
          })}
          <div className="h-px bg-[#F1F5F9] my-1.5" />
          <button className={row(items.length - 1)} onMouseEnter={() => setActive(items.length - 1)} onClick={() => go(items[items.length - 1].go)} data-testid="suggest-see-all">
            <span className="w-9 h-9 rounded-[10px] bg-[#F1F5F9] flex items-center justify-center"><Search className="w-4 h-4 text-[#475569]" /></span>
            <span className="flex-1 text-[14px] font-semibold">See all results for “{q.trim()}”</span><ArrowRight className="w-4 h-4 text-[#0000EC]" />
          </button>
          <p className="px-3 pt-1.5 pb-1 text-[11px] text-[#94A3B8]">↑ ↓ to navigate · Enter to open · Esc to close</p>
        </div>
      )}
    </div>
  );
}
